import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Image,
  Button,
  Menu,
  ActionIcon,
  Drawer,
  Text,
  Divider,
  Burger,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPhone,
  IconMail,
  IconSun,
  IconMoon,
  IconChevronDown,
  IconHome,
  IconPackage,
  IconBuilding,
  IconMap,
  IconCertificate,
  IconMessageCircle,
  IconLock,
} from '@tabler/icons-react';
import { useAppSelector } from '../../store/hooks';
import { useTheme } from '../../contexts/ThemeContext';

const navIcons: Record<string, React.ReactNode> = {
  home: <IconHome size={18} />,
  products: <IconPackage size={18} />,
  about: <IconBuilding size={18} />,
  international: <IconMap size={18} />,
  certifications: <IconCertificate size={18} />,
  contact: <IconMessageCircle size={18} />,
};

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

export const Header: React.FC = () => {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { content } = useAppSelector((state) => state.content);
  const { companyInfo, navigation } = content;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = navigation.header;

  const renderNavItem = (item: any) => {
    const Icon = navIcons[item.id];

    if (item.id === 'home') {
      return (
        <Link key={item.id} to="/" className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 ${location.pathname === '/' ? 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-green-700 dark:hover:text-green-400'}`}>
          {Icon}
          <span>Home</span>
        </Link>
      );
    }

    if (item.id === 'products') {
      return (
        <Link key={item.id} to="/products" className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 ${location.pathname.startsWith('/products') ? 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-green-700 dark:hover:text-green-400'}`}>
          {Icon}
          <span>Products</span>
        </Link>
      );
    }

    if (item.id === 'about') {
      const isAboutActive = location.pathname === '/about' || location.pathname === '/international' || location.pathname === '/certifications';
      return (
        <Menu key={item.id} trigger="hover" withinPortal>
          <Menu.Target>
            <button className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 ${isAboutActive ? 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-green-700 dark:hover:text-green-400'}`}>
              {Icon}
              <span>About Us</span>
              <IconChevronDown size={14} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item component={Link} to="/about">About Us</Menu.Item>
            <Menu.Item component={Link} to="/international">International</Menu.Item>
            <Menu.Item component={Link} to="/certifications">Certifications</Menu.Item>
            <Menu.Item component={Link} to="/admin/login">Login</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      );
    }

    if (item.id === 'contact') {
      return (
        <Link key={item.id} to="/contact" className="px-3 py-2 text-sm font-medium flex items-center gap-1.5 text-gray-700 dark:text-gray-300 hover:text-green-600">
          {Icon}
          Contact
        </Link>
      );
    }

    return null;
  };

  return (
    <header className="h-20 md:h-24 bg-gray-100 dark:bg-gray-900 border-b sticky top-0 z-50">
      <Container size="xl" className="h-full px-4 sm:px-6 lg:px-8">
        <div className="h-full flex items-center justify-between">

          {/* ✅ STATIC LOGO (NO DELAY + SAME SIZE) */}
          <Link to="/" className="no-underline">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="relative w-60 h-28 md:w-80 md:h-36 lg:w-96 lg:h-44 flex-shrink-0">
                <Image
                  src="/assets/images/logo.png"
                  alt={companyInfo.name}
                  className="h-full w-auto object-contain"
                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                />
              </div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map(renderNavItem)}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <ActionIcon onClick={toggleTheme}>
              {isDarkMode ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <ActionIcon onClick={toggleTheme}>
              {isDarkMode ? <IconSun size={18} /> : <IconMoon size={18} />}
            </ActionIcon>
            <Burger opened={drawerOpened} onClick={toggleDrawer} size="sm" />
          </div>
        </div>
      </Container>

      <Drawer opened={drawerOpened} onClose={closeDrawer} position="right" size="100%">
        <div className="flex flex-col gap-2">

          {/* ✅ Drawer Logo */}
          <div className="w-8 h-8 relative">
            <Image src="/assets/images/logo.png" alt={companyInfo.name} className="w-full h-full object-contain" />
          </div>

          {navItems.map((item: any) => (
            <Link key={item.id} to={item.link} onClick={closeDrawer}>
              <Text>{item.label}</Text>
            </Link>
          ))}

          <Divider />

          <Button component="a" href={`tel:${companyInfo.phone}`}>Call</Button>
          <Button component="a" href={`mailto:${companyInfo.email}`}>Email</Button>
        </div>
      </Drawer>
    </header>
  );
};